/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const MODES = [
  { id: "tambah", name: "Penjumlahan", icon: "➕", generate: () => { const a = Math.floor(Math.random()*20)+1; const b = Math.floor(Math.random()*20)+1; return { q: `${a} + ${b} = ?`, answer: a+b, display: `${a} + ${b}` }; }},
  { id: "kurang", name: "Pengurangan", icon: "➖", generate: () => { const a = Math.floor(Math.random()*20)+5; const b = Math.floor(Math.random()*a)+1; return { q: `${a} - ${b} = ?`, answer: a-b, display: `${a} - ${b}` }; }},
  { id: "kali", name: "Perkalian", icon: "✖️", generate: () => { const a = Math.floor(Math.random()*10)+1; const b = Math.floor(Math.random()*10)+1; return { q: `${a} × ${b} = ?`, answer: a*b, display: `${a} × ${b}` }; }},
  { id: "campur", name: "Campur", icon: "🔀", generate: () => { const ops = [0,1,2]; const op = ops[Math.floor(Math.random()*3)]; if (op===0) { const a=Math.floor(Math.random()*20)+1; const b=Math.floor(Math.random()*20)+1; return { q:`${a}+${b}=?`, answer:a+b, display:`${a} + ${b}` }; } if (op===1) { const a=Math.floor(Math.random()*20)+5; const b=Math.floor(Math.random()*a)+1; return { q:`${a}-${b}=?`, answer:a-b, display:`${a} - ${b}` }; } const a=Math.floor(Math.random()*10)+1; const b=Math.floor(Math.random()*10)+1; return { q:`${a}×${b}=?`, answer:a*b, display:`${a} × ${b}` }; }},
];

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const GameHitung = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [playerCount, setPlayerCount] = useState(1); // 1 = solo, 2 = berdua
  const [mode, setMode] = useState(0);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [scores, setScores] = useState([0, 0]);
  const [round, setRound] = useState(0);
  const [totalRounds] = useState(30);
  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [streak, setStreak] = useState(0);
  const [usedQuestions, setUsedQuestions] = useState([]); // track recent question keys to avoid duplicates

  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const startGame = () => { savePlayerNames(playerNames); setScores([0,0]); setRound(0); setCurrentPlayer(0); setStreak(0); setUsedQuestions([]); setGameState("playing"); };

  const nextQuestion = useCallback(() => {
    // Generate a question avoiding recent duplicates (try up to 20 attempts)
    let q = MODES[mode].generate();
    let attempts = 0;
    while (usedQuestions.includes(q.q) && attempts < 20) {
      q = MODES[mode].generate();
      attempts++;
    }
    setUsedQuestions(prev => {
      const next = [...prev, q.q];
      return next.length > 25 ? next.slice(-25) : next;
    });
    const wrongs = new Set();
    while (wrongs.size < 3) {
      const w = q.answer + (Math.floor(Math.random()*7) - 3);
      if (w !== q.answer && w >= 0) wrongs.add(w);
    }
    const opts = [...wrongs, q.answer].sort(() => Math.random()-0.5);
    setQuestion(q);
    setOptions(opts);
    setFeedback(null);
    if (soundOn) {
      const who = playerCount === 1 ? p1 : (currentPlayer === 0 ? p1 : p2);
      speakText(`${who}, what is ${q.display}?`);
    }
  }, [mode, currentPlayer, p1, p2, soundOn, playerCount, usedQuestions]);

  useEffect(() => { if (gameState === "playing") nextQuestion(); }, [gameState]);
  useEffect(() => { if (gameState === "playing" && round > 0) nextQuestion(); }, [round]);

  const handleAnswer = (ans) => {
    if (feedback) return;
    if (ans === question.answer) {
      const bonus = streak >= 2 ? 2 : 1;
      setFeedback({ type: "correct", text: `Benar! +${bonus}` });
      setScores(prev => { const n = [...prev]; n[currentPlayer] += bonus; return n; });
      setStreak(prev => prev + 1);
      if (soundOn) speakText(`Correct! ${question.display} equals ${question.answer}!`);
    } else {
      setFeedback({ type: "wrong", text: `${question.answer}!` });
      setStreak(0);
      if (soundOn) speakText(`The answer is ${question.answer}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) {
        setGameState("won");
        recordGameWon("hitung");
        if (soundOn) {
          if (playerCount === 1) speakText(`You got ${scores[0] + (ans === question.answer ? (streak >= 2 ? 2 : 1) : 0)} of ${totalRounds}!`);
          else speakText(scores[0] > scores[1] ? `${p1} wins!` : scores[1] > scores[0] ? `${p2} wins!` : "Draw!");
        }
      } else {
        setRound(prev => prev + 1);
        if (playerCount === 2) setCurrentPlayer(prev => prev === 0 ? 1 : 0);
      }
    }, 1500);
  };

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-4 pt-4" data-testid="game-hitung-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ rotate: [0,10,-10,0] }} transition={{ duration: 2, repeat: Infinity }}>🧮</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Game Hitung</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-3">Jawab soal matematika secepat mungkin!</p>

          <div className="flex gap-2 mb-3 justify-center">
            <Button data-testid="hitung-pc-1" onClick={() => setPlayerCount(1)} className={`flex-1 font-fredoka font-bold rounded-lg ${playerCount === 1 ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>👤 Sendiri</Button>
            <Button data-testid="hitung-pc-2" onClick={() => setPlayerCount(2)} className={`flex-1 font-fredoka font-bold rounded-lg ${playerCount === 2 ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>👥 Berdua</Button>
          </div>

          <div className={`grid ${playerCount === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mb-3`}>
            <input data-testid="hitung-p1" type="text" value={playerNames[0]} onChange={e => setPlayerNames([e.target.value, playerNames[1]])} placeholder="Nama Pemain" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            {playerCount === 2 && (
              <input data-testid="hitung-p2" type="text" value={playerNames[1]} onChange={e => setPlayerNames([playerNames[0], e.target.value])} placeholder="Pemain 2" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            )}
          </div>
          <div className="flex flex-wrap gap-2 mb-4 justify-center">
            {MODES.map((m, i) => (
              <Button key={m.id} onClick={() => setMode(i)} className={`font-fredoka text-xs rounded-lg ${mode === i ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>{m.icon} {m.name}</Button>
            ))}
          </div>
          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500" data-testid="hitung-start">MULAI HITUNG!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  if (gameState === "won") {
    const winner = playerCount === 1 ? null : (scores[0] > scores[1] ? p1 : scores[1] > scores[0] ? p2 : null);
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="game-hitung-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🏆</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">
            {playerCount === 1 ? `Skor: ${scores[0]}/${totalRounds}` : (winner ? `${winner} Menang!` : "Seri!")}
          </h2>
          <p className="font-fredoka text-gold-light/80 mb-4">
            {playerCount === 1 ? `${p1} dapat ${scores[0]} dari ${totalRounds} soal` : `${p1}: ${scores[0]} | ${p2}: ${scores[1]}`}
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setGameState("playing"); setScores([0,0]); setRound(0); setCurrentPlayer(0); setStreak(0); setUsedQuestions([]); }} className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={() => setGameState("menu")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3" data-testid="game-hitung-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🧮 {round+1}/{totalRounds}</span>
          <span className="font-fredoka text-cyan-400 text-xs">{p1}: {scores[0]}</span>
          {playerCount === 2 && <span className="font-fredoka text-pink-400 text-xs">{p2}: {scores[1]}</span>}
          {streak >= 2 && <span className="font-fredoka text-yellow-400 text-xs">🔥x{streak}</span>}
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      {playerCount === 2 && (
        <div className="font-fredoka text-sm mb-3">
          <span className={currentPlayer === 0 ? "text-cyan-400 font-bold" : "text-pink-400 font-bold"}>Giliran {currentPlayer === 0 ? p1 : p2}</span>
        </div>
      )}

      {question && (
        <motion.div key={round} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="glass-card-strong rounded-2xl p-8 text-center mb-4 w-full max-w-md">
          <p className="font-fredoka text-5xl sm:text-6xl text-gold font-bold mb-2">{question.display}</p>
          <p className="font-fredoka text-2xl text-gold-light/60">= ?</p>
        </motion.div>
      )}

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3">
            <span className={`font-fredoka font-bold text-xl px-6 py-2 rounded-full ${feedback.type === 'correct' ? 'bg-green-500 text-white' : 'bg-red-400 text-white'}`}>
              {feedback.type === 'correct' ? `✅ ${feedback.text}` : `❌ Jawaban: ${feedback.text}`}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3 w-full max-w-md">
        {options.map((opt, i) => (
          <motion.button key={`${opt}-${i}`} data-testid={`hitung-option-${i}`}
            onClick={() => handleAnswer(opt)} disabled={!!feedback}
            className="ingredient-3d quiz-option-btn-large text-center font-fredoka font-bold text-slate-800"
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.9 }}>
            {opt}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default GameHitung;
